import { Anchor, Button, Group, Stack, Text, TextInput, Title } from '@mantine/core'
import { hasLength, isEmail, isNotEmpty } from '@mantine/form'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { IconCheck } from '@tabler/icons-react'
import { useMutation } from '@tanstack/react-query'
import { useRef, useState } from 'react'

import { useGlobalState } from '@/hooks/useGlobalState'
import type { CreateCustomerInfoParams } from '@/services/api/api.types'
import { ERROR_MESSAGES, MIN_NAME_LENGTH, SUCCESS_MESSAGES } from '@/utils/onboardingConstants'
import { handleOnboardingError, showOnboardingErrorNotification } from '@/utils/onboardingErrorHandling'

import { FriendlyCaptcha, type FriendlyCaptchaRef } from './FriendlyCaptcha'

export type SignUpFormValues = {
  orgName: string
  contactName: string
  contactTitle?: string // Optional - not collected in form but kept for API compatibility
  contactEmail: string
  confirmEmail: string
  inviteCode?: string
}

interface SignUpFormProps {
  onSuccess: (customerInfoId: string, email: string) => void
}

export function SignUpForm({ onSuccess }: SignUpFormProps) {
  const { api, configStore } = useGlobalState()
  const [captchaResponse, setCaptchaResponse] = useState<string | null>(null)
  const captchaRef = useRef<FriendlyCaptchaRef>(null)

  const captchaSitekey = configStore.captchaSitekey || 'test-sitekey'

  const form = useForm<SignUpFormValues>({
    initialValues: {
      orgName: '',
      contactName: '',
      contactEmail: '',
      confirmEmail: '',
      inviteCode: '',
    },
    validate: {
      orgName: hasLength({ min: MIN_NAME_LENGTH }, ERROR_MESSAGES.ORG_NAME_TOO_SHORT),
      contactName: hasLength({ min: MIN_NAME_LENGTH }, ERROR_MESSAGES.CONTACT_NAME_TOO_SHORT),
      contactEmail: isEmail(ERROR_MESSAGES.EMAIL_INVALID),
      confirmEmail: (value: string, values: SignUpFormValues) => {
        if (!isNotEmpty(value)) return ERROR_MESSAGES.CONFIRM_EMAIL_REQUIRED
        if (value !== values.contactEmail) return ERROR_MESSAGES.EMAIL_MISMATCH
        return null
      },
    },
    validateInputOnBlur: true,
  })

  const createCustomerMutation = useMutation({
    mutationFn: async (params: CreateCustomerInfoParams & { email: string }) => {
      const { email, ...apiParams } = params

      // Step 1: Create customer info
      const customerResponse = await api.createCustomerInfo(apiParams)

      // Step 2: Send confirmation email
      await api.sendConfirmationEmail({
        customer_info_uuid: customerResponse.customerInfoUuid,
      })

      return { ...customerResponse, email }
    },
    onSuccess: response => {
      notifications.show({
        title: 'Success',
        message: SUCCESS_MESSAGES.ACCOUNT_CREATED,
        color: 'green',
        icon: <IconCheck size={18} />,
      })
      onSuccess(response.customerInfoUuid, response.email)
    },
    onError: error => {
      const onboardingError = handleOnboardingError(error, 'create-account')
      showOnboardingErrorNotification(onboardingError, 'account creation')

      // Reset captcha state and widget on error
      setCaptchaResponse(null)
      captchaRef.current?.reset()
    },
  })

  const handleSubmit = (values: SignUpFormValues) => {
    if (!captchaResponse) {
      notifications.show({
        title: 'Captcha Required',
        message: ERROR_MESSAGES.CAPTCHA_REQUIRED,
        color: 'red',
      })
      return
    }

    const params: CreateCustomerInfoParams & { email: string } = {
      org_name: values.orgName,
      contact_name: values.contactName,
      contact_title: values.contactTitle || '',
      contact_email: values.contactEmail,
      'frc-captcha-response': captchaResponse,
      email: values.contactEmail,
    }

    // Add invite code if provided
    if (values.inviteCode && values.inviteCode.trim() !== '') {
      params['invite-code'] = values.inviteCode.trim()
    }

    createCustomerMutation.mutate(params)
  }

  return (
    <Stack gap="md" mt="xl" maw={768} mx="auto">
      <Title order={6} ta="center">
        Create Your Channel
      </Title>

      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md" my="xl">
          <TextInput
            label="Channel Name"
            placeholder="Your Organization Name"
            required
            {...form.getInputProps('orgName')}
          />

          <TextInput
            label="Contact Name"
            placeholder="Your full name"
            required
            {...form.getInputProps('contactName')}
          />

          <TextInput
            label="Contact Email"
            placeholder="your.email@company.com"
            type="email"
            required
            {...form.getInputProps('contactEmail')}
          />

          <TextInput
            label="Confirm Email"
            placeholder="Confirm your email address"
            type="email"
            required
            {...form.getInputProps('confirmEmail')}
          />

          <TextInput
            label="Invite Code"
            placeholder="Enter your invite code (optional)"
            {...form.getInputProps('inviteCode')}
          />

          <Group mt="xl">
            <FriendlyCaptcha
              ref={captchaRef}
              sitekey={captchaSitekey}
              onVerify={setCaptchaResponse}
              onError={_error => {
                notifications.show({
                  title: 'Captcha Error',
                  message: ERROR_MESSAGES.CAPTCHA_ERROR,
                  color: 'red',
                })
              }}
            />
          </Group>

          <Text size="sm" c="dimmed" mt="md">
            By creating a channel, you agree to the{' '}
            <Anchor href="https://sidekickwellness.com/tos.html" target="_blank" rel="noopener noreferrer">
              Terms of Service
            </Anchor>
            . For more information about Sidekick Wellness&apos; privacy practices, see the{' '}
            <Anchor href="https://sidekickwellness.com/privacy.html" target="_blank" rel="noopener noreferrer">
              Sidekick Wellness Privacy Statement
            </Anchor>
            . We&apos;ll occasionally send you account-related emails.
          </Text>

          <Group justify="flex-end" mt="xl">
            <Button
              type="submit"
              loading={createCustomerMutation.isPending}
              disabled={!form.isValid() || !captchaResponse}>
              Next
            </Button>
          </Group>
        </Stack>
      </form>
    </Stack>
  )
}
